export const MicrochipStatusValues = {
  CHIPPED: 'chipped',
  NOT_CHIPPED: 'not_chipped',
  UNKNOWN: 'unknown',
} as const
export type MicrochipStatus =
  (typeof MicrochipStatusValues)[keyof typeof MicrochipStatusValues]

export const PatientFieldName = {
  AdditionalIdentification: 'additional_identification',
  AnimalCode: 'animal_code',
  Avatar: 'avatar',
  BloodGroup: 'blood_group',
  Breed: 'breed',
  BreedId: 'breed_id',
  City: 'city',
  Color: 'color',
  CountryRegion: 'country_region',
  CPRAgreement: 'cpr_agreement',
  CprStatus: 'cpr_status',
  DateOfBirth: 'date_of_birth',
  DateOfDeath: 'date_of_death',
  DateOfRegistration: 'date_of_registration',
  Deceased: 'deceased',
  FarmStableName: 'stable_name',
  Gender: 'gender',
  HoldingPlaceNumber: 'holding_place_number',
  HomeDepartment: 'home_department',
  Insurance: 'insurance',
  InsuranceCompany: 'insurance_company_id',
  InsuranceExcessFeeAmount: 'insurance_excess_fee_amount',
  InsuranceExcessFeeType: 'insurance_excess_fee_type',
  InsurancePolicyEndDate: 'insurance_policy_end_date',
  Microchip: 'microchip',
  MicrochipDate: 'microchip_date',
  Nickname: 'name',
  NotForFood: 'not_for_food',
  OfficialName: 'official_name',
  PassportDate: 'passport_given',
  PassportNumber: 'passport_number',
  PatientTypes: 'patient_types',
  PrimaryClinicLocation: 'primary_clinic_location',
  PrivacySettings: 'private',
  ReasonOfDeath: 'reason_of_death',
  RegisterNumber: 'register_number',
  Remarks: 'remarks',
  Species: 'species',
  SpeciesId: 'species_id',
  State: 'state',
  Statuses: 'statuses',
  StreetAddress: 'street_address',
  StreetAddress2: 'street_address_2',
  Tags: 'tags',
  VetenvoyActivated: 'vetenvoy_activated',
  VetenvoyIsBreeder: 'vetenvoy_is_breeder',
  VetenvoyProvider: 'vetenvoy_provider',
  VisibleDepartments: 'visible_departments',
  VisibleDepartmentGroups: 'visible_department_groups',
  ZipCode: 'zip_code',
} as const

export type ApiPatientFieldName =
  (typeof PatientFieldName)[keyof typeof PatientFieldName]

/** Canonical order for patient details fields that split between main section and accordion. */
export const patientDetailsDynamicFieldOrder = [
  PatientFieldName.Nickname,
  PatientFieldName.Microchip,
  PatientFieldName.Species,
  PatientFieldName.Gender,
  PatientFieldName.Color,
  PatientFieldName.DateOfBirth,
  PatientFieldName.Remarks,
  PatientFieldName.HomeDepartment,
] as const

export type PatientDetailsDynamicSlot =
  (typeof patientDetailsDynamicFieldOrder)[number]

/** Always shown in the main section regardless of required status. */
export const patientDetailsDynamicPinnedSlots =
  new Set<PatientDetailsDynamicSlot>([
    PatientFieldName.Microchip,
    PatientFieldName.Color,
    PatientFieldName.DateOfBirth,
  ])

/** Pairs that move together for required/optional resolution (first key is in field order). */
export const patientDetailsDynamicFormFieldPairs: ReadonlyArray<
  readonly [PatientDetailsDynamicSlot, ApiPatientFieldName]
> = [
  [PatientFieldName.Nickname, PatientFieldName.OfficialName],
  [PatientFieldName.Species, PatientFieldName.Breed],
  [PatientFieldName.Remarks, PatientFieldName.Tags],
]

export const PATIENT_FIELD_NAME_TO_FORM_FIELD: Partial<
  Record<ApiPatientFieldName, string>
> = {
  [PatientFieldName.Nickname]: 'name',
  [PatientFieldName.OfficialName]: 'officialName',
  [PatientFieldName.DateOfBirth]: 'dateOfBirth',
  [PatientFieldName.DateOfRegistration]: 'dateOfRegistration',
  [PatientFieldName.Species]: 'species',
  [PatientFieldName.SpeciesId]: 'species',
  [PatientFieldName.Breed]: 'breed',
  [PatientFieldName.BreedId]: 'breed',
  [PatientFieldName.Gender]: 'gender',
  [PatientFieldName.BloodGroup]: 'bloodGroup',
  [PatientFieldName.Color]: 'color',
  [PatientFieldName.CprStatus]: 'cprStatus',
  [PatientFieldName.CPRAgreement]: 'cprStatus',
  [PatientFieldName.AdditionalIdentification]: 'additionalIdentification',
  [PatientFieldName.FarmStableName]: 'farmStableName',
  [PatientFieldName.HoldingPlaceNumber]: 'holdingPlaceNumber',
  [PatientFieldName.HomeDepartment]: 'homeDepartment',
  [PatientFieldName.Microchip]: 'microchip',
  [PatientFieldName.MicrochipDate]: 'microchipDate',
  [PatientFieldName.PassportNumber]: 'passport',
  [PatientFieldName.PassportDate]: 'passportDate',
  [PatientFieldName.RegisterNumber]: 'registerNumber',
  [PatientFieldName.AnimalCode]: 'animalCode',
  [PatientFieldName.Remarks]: 'remarks',
  [PatientFieldName.Insurance]: 'insuranceNumber',
  [PatientFieldName.InsuranceCompany]: 'insuranceCompany',
  [PatientFieldName.InsuranceExcessFeeAmount]: 'insuranceExcessFeeAmount',
  [PatientFieldName.InsuranceExcessFeeType]: 'insuranceExcessFeeType',
  [PatientFieldName.InsurancePolicyEndDate]: 'insurancePolicyEndDate',
  [PatientFieldName.NotForFood]: 'notForFood',
  [PatientFieldName.PatientTypes]: 'type',
  [PatientFieldName.PrivacySettings]: 'isPrivate',
  [PatientFieldName.VetenvoyActivated]: 'vetenvoyActivated',
  [PatientFieldName.VetenvoyIsBreeder]: 'vetenvoyIsBreeder',
  [PatientFieldName.VetenvoyProvider]: 'vetenvoyProvider',
  [PatientFieldName.VisibleDepartments]: 'visibleForDepartments',
  [PatientFieldName.VisibleDepartmentGroups]: 'visibleForDepartmentGroups',
}

/**
 * Compute microchip status from patient data.
 *
 * @param microchip - The patient's microchip ID
 * @param hasMicrochipCheck - Whether the patient has a microchip_check relationship
 */
export function computeMicrochipStatus(
  microchip: string | null | undefined,
  hasMicrochipCheck: boolean,
): MicrochipStatus {
  if (microchip) {
    return 'chipped'
  }
  if (hasMicrochipCheck) {
    return 'not_chipped'
  }
  return 'unknown'
}

export type FieldVisibility = {
  additionalIdentification?: boolean
  animalCode?: boolean
  avatar?: boolean
  bloodGroup?: boolean
  breed?: boolean
  city?: boolean
  color?: boolean
  countryRegion?: boolean
  cprStatus?: boolean
  dateOfBirth?: boolean
  dateOfDeath?: boolean
  dateOfRegistration?: boolean
  farmStableName?: boolean
  gender?: boolean
  holdingPlaceNumber?: boolean
  homeDepartment?: boolean
  identification?: boolean
  insurance?: boolean
  insuranceCompany?: boolean
  insuranceExcessFeeAmount?: boolean
  insuranceExcessFeeType?: boolean
  insurancePolicyEndDate?: boolean
  microchip?: boolean
  microchipDate?: boolean
  microchipPlacement?: boolean
  nickname?: boolean
  notForFood?: boolean
  officialName?: boolean
  passportDate?: boolean
  passportNumber?: boolean
  patientTypes?: boolean
  primaryClinicLocation?: boolean
  privacySettings?: boolean
  registerNumber?: boolean
  reasonOfDeath?: boolean
  remarks?: boolean
  species?: boolean
  state?: boolean
  statuses?: boolean
  streetAddress?: boolean
  streetAddress2?: boolean
  tags?: boolean
  vetenvoyActivated?: boolean
  vetenvoyIsBreeder?: boolean
  vetenvoyProvider?: boolean
  visibleDepartments?: boolean
  visibleDepartmentGroups?: boolean
  zipCode?: boolean
}

/**
 * Fallback visibility used when UI config is unavailable.
 *
 * This intentionally fails open so missing config does not hide patient form
 * functionality.
 */
export const DEFAULT_PATIENT_FIELD_VISIBILITY: FieldVisibility = {
  additionalIdentification: true,
  animalCode: true,
  bloodGroup: true,
  breed: true,
  city: true,
  color: true,
  countryRegion: true,
  cprStatus: true,
  dateOfBirth: true,
  dateOfRegistration: true,
  farmStableName: true,
  gender: true,
  holdingPlaceNumber: true,
  homeDepartment: true,
  identification: true,
  insurance: true,
  insuranceCompany: true,
  insuranceExcessFeeAmount: true,
  insuranceExcessFeeType: true,
  insurancePolicyEndDate: true,
  microchip: true,
  microchipDate: true,
  microchipPlacement: true,
  nickname: true,
  notForFood: true,
  officialName: true,
  passportDate: true,
  passportNumber: true,
  privacySettings: true,
  registerNumber: true,
  remarks: true,
  species: true,
  state: true,
  streetAddress: true,
  streetAddress2: true,
  tags: true,
  visibleDepartments: true,
  visibleDepartmentGroups: true,
  zipCode: true,

  // VetEnvoy fields
  // these are not visible by default, but can be enabled by the UI config
  vetenvoyActivated: false,
  vetenvoyIsBreeder: false,
  vetenvoyProvider: false,

  // not visible fields
  avatar: false,
  dateOfDeath: false,
  patientTypes: false,
  primaryClinicLocation: false,
  reasonOfDeath: false,
  statuses: false,
}

export type SimplePatient = {
  id: string
  icon: string
  name: string
  avatar?: string
  criticalNotes?: string
}

export type PatientPrefillData = {
  additionalIdentification?: string
  bloodGroup?: string
  breedId?: number | string
  color?: string
  cprStatus?: string
  dateOfBirth?: string
  earNotched?: string
  earNotchedDate?: string
  earNotchedPlacement?: string
  farmStableName?: string
  freezeMarking?: string
  freezeMarkingDate?: string
  freezeMarkingPlacement?: string
  gender?: string
  hotBrandedId?: string
  hotBrandedIdDate?: string
  hotBrandedIdPlacement?: string
  microchip?: string
  microchipDate?: string
  microchipPlacement?: string
  name?: string
  paintBranding?: string
  paintBrandingDate?: string
  paintBrandingPlacement?: string
  passport?: string
  passportDate?: string
  passportNumber?: string
  speciesId?: number | string
  tagId?: string
  tagIdDate?: string
  tagIdPlacement?: string
  tattoedId?: string
  tattoedIdDate?: string
  tattoedIdPlacement?: string
  vetenvoyActivated?: boolean
  vetenvoyIsBreeder?: boolean
  vetenvoyProvider?: string | number | null
}
